import { Action, TimeString, Uri } from '@nextforce/api'
import { BaseSetting, Setting, SettingValue } from 'settings/types'
import * as Response from './response'
import { StoreSettingsData, UserSettingsData } from './types'

export function convertUserSettings(
  response: Response.Settings,
  uri: Uri
): UserSettingsData {
  const action: Action = {
    method: 'update',
    uri,
    params: {
      preferences: [],
    },
  }

  const settings: Setting[] = convertPreferences(response.preferences, action)
  return { settings }
}

export function convertStoreSettings(
  response: Response.StoreSettings,
  uri: Uri
): StoreSettingsData {
  const action: Action = {
    method: 'update',
    uri,
    params: {
      profile: [],
    },
  }

  const settings: Setting[] = convertPreferences(response.profile, action)
  return { settings, title: response.title }
}

function convertPreferences(
  preferences: Response.Preference[],
  action: Action
): Setting[] {
  const settings = preferences.flatMap(preference => {
    const baseSetting: BaseSetting = {
      id: preference.name,
      name: preference.display_name,
      shortName: preference.short_display_name,
      description: preference.description,
      updated: preference.touched ?? false,
      required: preference.required ?? false,
      actions: {
        update: action,
      },
    }

    if (preferences.find(pref => pref.name === preference.name + '_toggle')) {
      baseSetting.toggledBy = preference.name + '_toggle'
    }

    switch (preference.type) {
      case 'single-select':
        let selectedId
        const values: SettingValue[] = preference?.values.flatMap(value => {
          if (value.selected) {
            selectedId = value.name
          }
          return [
            {
              id: value.name,
              name: value.display_name,
              locked: value.locked ?? false,
            },
          ]
        })
        return [
          {
            type: 'singleSelect',
            values,
            selectedId,
            ...baseSetting,
          },
        ]
      case 'time-range':
        const times: TimeString[] = preference.values[0].name.split('-')
        return [
          {
            type: 'timeRange',
            startTime: times[0],
            endTime: times[1],
            ...baseSetting,
          },
        ]
      case 'boolean':
        const isOn = preference?.values.some(
          value => value.name === 'true' && value.selected
        )
        return [
          {
            type: 'boolean',
            on: isOn,
            ...baseSetting,
          },
        ]
      case 'text':
        return [
          {
            type: 'text',
            text: preference.values[0].name,
            ...baseSetting,
          },
        ]
      case 'dollars':
        return [
          {
            type: 'dollars',
            text: preference.values[0].name,
            ...baseSetting,
          },
        ]
      case 'number':
        return [
          {
            type: 'number',
            number: preference.values[0].name,
            ...baseSetting,
          },
        ]
      case 'multi-select':
        const selectedIds: String[] = []
        const multiSelectValues:SettingValue[] = preference?.values.flatMap(value => {
          if (value.selected) {
            selectedIds.push(value.name)
          }
          return [
            {
              id: value.name,
              name: value.display_name,
              locked: value.locked ?? false,
            },
          ]
        })
        return [
          {
            type: 'multiSelect',
            selectedIds: selectedIds,
            values: multiSelectValues,
            ...baseSetting,
          },
        ]
      default:
        return [] as any
    }
  })

  return settings
}
